import './UsersPanel.scss';

import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { KeyPagination } from '@ui/KeyPagination';
import { ShardSearchEntryDetails } from '@sonar-software/sonar-admin';
import { SonarTable } from '@ui/SonarTable';
import { UserTableSearch } from './UserTableSearch';

import { getUserTableColumns, getUserTableRowKey } from './UsersTableColumns';
import { KeyPaginationQueryParams, PaginationDirection } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';

const getCustomerUsersSelectors = createAsyncSelectors((state) => state.core.customers.byId.users.get);

export interface UsersTableProps {
	defaultPageSize?: number;
	id?: string;
	searchDetails?: ShardSearchEntryDetails;
}

export interface UserEmailFilterFormValues {
	filter: string;
}

export const UsersTable: React.FC<UsersTableProps> = ({ defaultPageSize = 20, id, searchDetails }) => {
	const [ emailFilter, setEmailFilter ] = useState('');

	const customerUsers = useSonarSelector(getCustomerUsersSelectors.data);

	const getCustomerUsers = useAction(coreActions.requestGetCoreCustomersByIdUsers);

	const handleEmailFilterChanged = useCallback((value: string) => {
		setEmailFilter(value);
	}, []);

	const getCustomerUsersPage = useCallback(
		(
			count: number = defaultPageSize,
			paginationDirection: PaginationDirection = PaginationDirection.Forward,
			key?: string
		) => {
			if (searchDetails) {
				const { customerId, shardName } = searchDetails;

				getCustomerUsers({
					count: count ?? defaultPageSize,
					customerId,
					filter: emailFilter,
					key,
					paginationDirection,
					shardName
				});
			}
		},
		[ defaultPageSize, searchDetails, getCustomerUsers, emailFilter ]
	);

	const handlePaginationChanged = useCallback(
		(pagination: Partial<KeyPaginationQueryParams>) => {
			if (customerUsers) {
				const paginationDirection = pagination.paginationDirection ?? PaginationDirection.Forward;
				const key =
					pagination.paginationDirection === PaginationDirection.Forward ?
						customerUsers?.nextKey :
						customerUsers?.prevKey;

				getCustomerUsersPage(pagination.count, paginationDirection, key);
			}
		},
		[ getCustomerUsersPage, customerUsers ]
	);

	useEffect(() => {
		getCustomerUsersPage();
	}, [ getCustomerUsersPage ]);

	useEffect(() => {
		setEmailFilter(searchDetails?.userEmail ?? '');
	}, [ searchDetails?.userEmail ]);

	const tableColumns = useMemo(() => getUserTableColumns(searchDetails?.shardName), [ searchDetails?.shardName ]);

	return (
		<div className='admin-users-table'>
			<UserTableSearch
				onSearch={ handleEmailFilterChanged }
				value={ emailFilter } />
			<SonarTable
				columns={ tableColumns }
				dataSource={ customerUsers?.results }
				id={ id }
				pagination={ false }
				rowKey={ (record) => getUserTableRowKey(record, searchDetails?.shardName) }
				scroll={ { x: 'max-content' } } />
			<KeyPagination
				autoHide
				defaultPageSize={ defaultPageSize }
				onChange={ handlePaginationChanged }
				resource={ customerUsers } />
		</div>
	);
};
